export function GetValueFromPpg(density: number, unit: string): number {
  switch (unit) {
    case 'ppg':
      return density;
    case 'kg/m³':
    case 'g/L':
      return +(density * 119.8264).toFixed(2);
    case 'g/cm³':
    case 'kg/l':
    case 'sg':
      return +(density * 0.1198264).toFixed(4);
    case 'g/m³':
      return +(density * 119826.4).toFixed(0);
  }
}

export function GetPpgFromValue(density: number, unit: string): number {
  switch (unit) {
    case 'ppg':
      return density;
    case 'kg/m³':
    case 'g/L':
      return +(density / 119.8264);
    case 'g/cm³':
    case 'kg/l':
    case 'sg':
      return +(density / 0.1198264);
    case 'g/m³':
      return +(density / 119826.4);
  }
}

export function GetValueFromPsi(pressure: number, unit: string, getFullValue?: boolean): number {
  switch (unit) {
    case 'psi':
      return getFullValue ? pressure : +pressure.toFixed(2);
  case 'bar':
      return getFullValue ? (pressure * 0.068947572932) : +(pressure * 0.068947572932).toFixed(2);
  case 'KPa':
      return getFullValue ? (pressure * 6.8947572932) : +(pressure * 6.8947572932).toFixed(2);
  case 'atm':
      return getFullValue ? (pressure / 14.696) : +(pressure / 14.696).toFixed(2);
  }
}

export function GetPsiFromValue(pressure: number, unit: string): number {
  switch (unit) {
    case 'psi':
      return pressure;
    case 'bar':
      return +(pressure / 0.068947572932).toFixed(2);
    case 'KPa':
      return +(pressure / 6.8947572932).toFixed(2);
    case 'atm':
      return +(pressure * 14.696).toFixed(2);
  }
}

export function GetSolidsDensityFromLbmFt(density: number, unit: string): number {
  switch (unit) {
    case 'lbm/ft³':
      return density;
    case 'kg/m³':
      return +(density * 16.0185).toFixed(2);
    case 'g/cm³':
      return +(density * 0.0160185).toFixed(2);
  }
}

export function GetValueFromFahrenheit(temperature: number, unit: string): number {
  switch (unit) {
    case '°F':
      return temperature;
    case '°C':
      return +((temperature - 32) / 1.8).toFixed(2);
    case 'K':
      return +((temperature - 32) / 1.8 + 273.15).toFixed(2);
  }
}

export function GetFahrenheitFromValue(temperature: number, unit: string): number {
  switch (unit) {
    case '°F':
      return temperature;
    case '°C':
      return +(temperature * 1.8 + 32);
    case 'K':
      return +((temperature - 273.15) * 1.8 + 32);
  }
}

export function GetGradientFahrenheitFeetFromValue(gradient: number, unit: string): number {
  switch (unit) {
    case '°F/100ft':
      return gradient;
    case '°C/100m':
      return +(gradient / 1.8226888304);
    case '°C/30m':
      return +(gradient * 1.8287)
  }
}

export function GetValueFromFeet(length: number, unit: string): number {
  switch (unit) {
    case 'ft':
      return length;
    case 'm':
      return +(length * 0.3048).toFixed(2);
  }
}

export function GetFeetFromValue(length: number, unit: string, skipRounding?: boolean): number {
  switch (unit) {
    case 'ft':
      return length;
    case 'm':
      return skipRounding ? +(length / 0.3048) : +(length / 0.3048).toFixed(2);
  }
}

export function GetShortLengthValueFromInches(length: number, unit: string): number {
  switch (unit) {
    case 'in':
      return length;
    case 'cm':
      return +(length * 2.54).toFixed(2);
    case 'mm':
      return +(length * 25.4).toFixed(2);
  }
}

export function GetInchesFromValue(length: number, unit: string): number {
  switch (unit) {
    case 'in':
      return length;
    case 'cm':
      return +(length / 2.54).toFixed(2);
    case 'mm':
      return +(length / 25.4).toFixed(2);
  }
}

export function GetForceValueFromLbf(force: number, unit: string): number {
  switch (unit) {
    case 'lbf':
      return force;
    case 'tf':
      return +(force * 0.00045359237).toFixed(2);
    case 'kgf':
      return +(force * 0.45359237).toFixed(2);
    case 'kip':
      return +(force * 0.001).toFixed(2);
    default:
      return force;
  }
}

export function getVolumeValueFromBbl(volume: number, unit: string): number {
  switch (unit) {
    case 'bbl':
      return volume;
    case 'm³':
      return +(volume * 0.1589873).toFixed(2);
    case 'l':
      return +(volume * 158.9873).toFixed(2);
  }
}

export function GetValueFromFt2(area: number, unit: string): number {
  switch (unit) {
    case 'ft²':
      return area;
    case 'm²':
      return +(area * 0.092903).toFixed(2);
    case 'cm²':
      return +(area * 929.0304).toFixed(2);
    case 'in²':
      return +(area * 144).toFixed(2);
  }
}

export function GetFeetCubicPerSecondFromValue(flowRate: number, unit: string): number {
  switch (unit) {
    case 'ft³/sec':
      return flowRate;
    case 'gpm':
      return +(flowRate * 0.002228); // Gallons/min → ft³/sec
    case 'm³/min':
      return +(flowRate * 0.5886); // m³/min → ft³/sec
    case 'bpm':
      return +(flowRate * 42 / 7.480519 / 60); // bpm → ft³/sec
    case 'bbl/d':
      return +(flowRate * 42 / 7.48051948 / 86400); // bbl/d → ft³/sec
    case 'L/min':
      return +(flowRate * 0.0353147 / 1000 / 60); // L/min → ft³/sec
  }
}

export function GetFlowRateValueFromGpm(flowRate: number, unit: string): number {
  switch (unit) {
    case 'gpm':
      return flowRate;
    case 'm³/min':
      return +(flowRate * 0.003785411784206);
    case 'bbl/d':
      return +(flowRate * 34.2857142857);
    case 'bpm':
      return +(flowRate * 0.0238095238095);
    case 'L/min':
      return +(flowRate * 3.785411784206);
  }
}

export function GetPressureLossFromPsi100ft(pressureLoss: number, unit: string): number {
  switch (unit) {
    case 'psi/100ft':
      return pressureLoss;
    case 'bar/1000m':
      return +(pressureLoss * 2.26205948);
  }
}

export function GetPsiPer100ftFromValue(pressureLoss: number, unit: string): number {
  switch (unit) {
    case 'psi/100ft':
      return pressureLoss;
    case 'bar/1000m':
      return +(pressureLoss / 2.26205948);
  }
}

export function GetcPFromValue(viscosity: number, unit: string): number {
  switch (unit) {
    case 'cP':
      return viscosity;
    case 'Pa/s':
      return +(viscosity * 1000);
  }
}

export function GetValueFromcP(viscosity: number, unit: string): number {
  switch (unit) {
    case 'cP':
      return viscosity;
    case 'Pa/s':
      return +(viscosity / 1000);
  }
}

export function GetYpValueFromlbf(yieldPoint: number, unit: string): number {
  switch (unit) {
    case 'lbf/100ft²':
      return yieldPoint;
    case 'Pa':
      return +(yieldPoint * 0.478802);
  }
}

export function GetValueFromFts(speed: number, unit: string): number {
  switch (unit) {
    case 'ft/s':
      return speed;
    case 'm/s':
      return +(speed * 0.3048);
  }
}

export function GetValueFromHp(power: number, unit: string): number {
  switch (unit) {
    case 'hp':
      return power;
    case 'BTU/h':
      return +(power * 2544.43).toFixed(2);
    case 'W':
      return +(power * 745.7).toFixed(2);
    case 'J/h':
      return +(power * 2684519.5).toFixed(2);
    default:
      return power;
  }
}

export function GetValueFromLbsKprime(kPrime: number, unit: string): number {
  switch (unit) {
    case "lb*s^n'/ft²":
      return kPrime;
    case 'Pa*s^n':
      return +(kPrime * 47.8802);
  }
}

// export function GetBarrelPerMinuteFromValue(flowRate: number, unit: string): number {
//   switch (unit) {
//     case 'bpm':
//       return flowRate;
//     case 'gpm':
//       return +(flowRate / 42);
//     case 'm³/min':
//       return +(flowRate * 0.1589873 / 42);
//   }
// }